/**
 * TestCaseFullScreenModal — renders TestCaseDetail at full viewport.
 * Triggered by the Expand button (⤢) or the F hotkey in TestCasesPage.
 *
 * The modal is intentionally wide (max-w-4xl) so the detail form has
 * breathing room. TestCaseDetail fills 100% width when isFullscreen=true.
 */

import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { TestCaseDetail } from './TestCaseDetail';
import type { TabId } from './TestCaseDetail';
import type { ITestCaseRow } from '../types';

interface TestCaseFullScreenModalProps {
    isOpen:      boolean;
    onClose:     () => void;
    testCase:    ITestCaseRow | null;
    projectId:   string | null;
    folderId?:   string | null;
    activeTab?:  TabId;
    onTabChange?: (tab: TabId) => void;
}

export function TestCaseFullScreenModal({
    isOpen,
    onClose,
    testCase,
    projectId,
    folderId,
    activeTab,
    onTabChange,
}: TestCaseFullScreenModalProps) {
    return (
        <Transition show={isOpen}>
            <Dialog onClose={onClose} className="relative z-50">
                {/* Backdrop */}
                <TransitionChild
                    enter="transition-opacity duration-200 ease-out"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition-opacity duration-150 ease-in"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
                </TransitionChild>

                {/* Panel */}
                <TransitionChild
                    enter="transition-all duration-200 ease-out"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="transition-all duration-150 ease-in"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                >
                    {/* Inset keeps a visible margin around the modal on large screens;
                        on mobile it goes nearly edge-to-edge */}
                    <div className="fixed inset-2 sm:inset-4 md:inset-8 flex items-stretch justify-center">
                        <DialogPanel data-testid="test-case-fullscreen-modal" aria-modal="true" className="w-full max-w-4xl bg-white dark:bg-gh-bg-dark rounded-2xl shadow-2xl overflow-hidden flex flex-col">
                            {/*
                             * TestCaseDetail with isFullscreen=true drops its fixed 400px
                             * width and fills this container completely. The onExpand callback
                             * is wired to onClose so clicking the Minimize icon collapses back.
                             */}
                            <TestCaseDetail
                                testCase={testCase}
                                projectId={projectId}
                                folderId={folderId}
                                isFullscreen={true}
                                onExpand={onClose}
                                onClose={onClose}
                                activeTab={activeTab}
                                onTabChange={onTabChange}
                            />
                        </DialogPanel>
                    </div>
                </TransitionChild>
            </Dialog>
        </Transition>
    );
}
